import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import jiraUtil from '../../components/jira/jira_util';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import DirectApi from '../../directApi';
import {Button, Checkbox} from 'web-components';
import RadioButton from '../new-ui/radio_button';
import styled from 'styled-components';
import Util from '../../forecast-app/shared/util/util';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import ChangeJiraRemainingSyncMutation from '../../mutations/ChangeJiraRemainingSyncMutation';
import UpdateProjectMutationModern from '../../mutations/update_project_mutation.modern';
import {BUTTON_COLOR, PROJECT_STATUS} from '../../constants';
import DatePickerV3 from '../../forecast-app/shared/components/date-picker/date_picker_v3';
import {DATE_PICKER_STYLE} from '../../constants';
import moment from 'moment';

const SyncRemainingSection = styled.div`
	margin-top: 10px;
	display: flex;
	flex-direction: column;
	row-gap: 10px;
`;

const CutoffDateSection = styled.div`
	margin-top: 10px;
	display: flex;
	flex-direction: column;
	row-gap: 10px;
`;

const DateControls = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const SyncWarning = styled.div`
	color: red;
	font-size: 12px;
	font-style: italic;
	margin-bottom: 16px;
`;

const JiraIntegrationSection = ({company, project}) => {
	const intl = useIntl();

	const [jiraEpics, setJiraEpics] = useState(null);
	const [syncRemaining, setSyncRemaining] = useState(project.jiraSyncRemaining);
	const [useCutoffDate, setUseCutoffDate] = useState(project.jiraCutoffDate != null);
	const [cutoffDate, setCutoffDate] = useState(project.jiraCutoffDate ? moment(project.jiraCutoffDate) : null);

	const retrieveLinkedEpics = epicIds => {
		if (!epicIds || epicIds.length === 0) return;
		DirectApi.Fetch(`jira/issues?issueIds=${epicIds}`).then(epics => setJiraEpics(epics));
	};

	useEffect(() => {
		retrieveLinkedEpics(project.jiraCloudEpicIds);
	}, []);

	useEffect(() => {
		setCutoffDate(project.jiraCutoffDate ? moment(project.jiraCutoffDate) : null);
		setUseCutoffDate(project.jiraCutoffDate != null);
	}, [project.jiraCutoffDate]);

	const handleUnlinkFromJira = () => {
		jiraUtil.unlinkProject(project);
	};

	const editJiraEpics = () => {
		showModal({
			type: MODAL_TYPE.JIRA_EDIT_EPICS,
			projectId: project.id,
			epicIds: project.jiraCloudEpicIds,
			retrieveLinkedEpics,
		});
	};

	const findTopLevelParentsPerChild = (tasks, child, lvl) => {
		return child.parentId
			? findTopLevelParentsPerChild(
					tasks,
					tasks.find(task => task.id === child.parentId),
					lvl + 1
			  )
			: {
					topLevelParent: child.id,
					atLevel: lvl,
			  };
	};

	const showSyncProjectModal = (syncType, subsubtasksPresent) => {
		showModal({
			type: MODAL_TYPE.SYNC_JIRA_PROJECT,
			jiraType: 'cloud',
			syncType,
			forecastProject: project,
			retrieveLinkedEpics,
			jiraProject: project.jiraCloudProject ? project.jiraCloudProject : null,
			edit: !!project.jiraCloudProject && hasFeatureFlag('edit_jira_cloud_project_sync_settings'),
			subsubtasksPresent,
		});
	};

	const syncJiraProject = syncType => {
		if (hasFeatureFlag('limit_task_level_depth')) {
			const tasks = project.tasks.edges.map(taskNode => {
				return {
					id: taskNode.node.id,
					parentId: taskNode.node.parentTaskId,
				};
			});

			//The tasks with level 1 -> task, 2 -> subtask and 3 -> subsubtask
			//filterede down to tasks with levels higher then 2 aka subsubtasks
			const subsubtasks = tasks
				.map(task => {
					return {
						id: task.id,
						...findTopLevelParentsPerChild(tasks, task, 1),
					};
				})
				.filter(task => task.atLevel > 2);

			//If any subsubtask in project
			const subsubtasksPresent = Array.isArray(subsubtasks) && !!subsubtasks.length;
			showSyncProjectModal(syncType, subsubtasksPresent);
		} else {
			showSyncProjectModal(syncType, false);
		}
	};

	const selectSyncRemainingTime = sync => {
		if (syncRemaining !== sync) {
			showModal({
				type: MODAL_TYPE.WARNING,
				message: 'Are you sure you want to change the "Synchronize remaining time" setting?',
				warningInformation: [
					<div>Changing this setting will initiate a sync of the project with the new settings.</div>,
				],
				buttons: [
					{
						text: <FormattedMessage id="common.cancel" />,
						color: BUTTON_COLOR.WHITE,
					},
					{
						text: <FormattedMessage id="common.confirm" />,
						callback: () => {
							setSyncRemaining(sync);
							const mutationObject = {
								forecastProjectId: project.id,
								syncRemaining: sync,
							};
							Util.CommitMutation(ChangeJiraRemainingSyncMutation, mutationObject);
						},
					},
				],
			});
		}
	};

	const projectDoneOrHalted = project.status === PROJECT_STATUS.DONE || project.status === PROJECT_STATUS.HALTED;

	const setCutoffDateHandler = cutoffDate => {
		Util.CommitMutation(UpdateProjectMutationModern, {
			id: project.id,
			jiraCutoffDate: cutoffDate ? cutoffDate.format('YYYY-MM-DD') : null,
		});
	};

	const setUseCutoffDateHandler = () => {
		if (useCutoffDate) {
			if (cutoffDate) {
				showModal({
					type: MODAL_TYPE.WARNING,
					message: 'Are you sure you want to remove the cutoff date?',
					warningInformation: [
						<div>
							Removing this date will cause issues and worklog to be synched to and from jira regardless of their
							date.
						</div>,
					],
					buttons: [
						{
							text: <FormattedMessage id="common.cancel" />,
							color: BUTTON_COLOR.WHITE,
						},
						{
							text: <FormattedMessage id="common.confirm" />,
							callback: () => {
								setCutoffDateHandler(null);
							},
						},
					],
				});
			} else {
				setUseCutoffDate(false);
			}
		} else {
			setUseCutoffDate(true);
		}
	};

	return (
		<div>
			{project.jiraCloudProject || project.jiraCloudEpicIds ? (
				<>
					{!project.jiraCloudProjectSettings.isSyncDone && (
						<SyncWarning>
							These options can not be changed while data sync is in progress. Please check back later.
						</SyncWarning>
					)}
					{project.jiraCloudProject ? (
						<div>
							<FormattedMessage
								id="settings.jira.linked_to_project"
								values={{name: <b>{project.jiraCloudProject.name}</b>}}
							/>
							{hasFeatureFlag('jira_sync_remaining') &&
								!hasFeatureFlag('edit_jira_cloud_project_sync_settings') && (
									<>
										<SyncRemainingSection>
											<FormattedMessage id="integrations.jira.sync_remaining" />
											<RadioButton
												text={intl.formatMessage({id: 'common.yes'})}
												onSelected={() => selectSyncRemainingTime(true)}
												value={true}
												stateValue={syncRemaining}
												locked={projectDoneOrHalted || !project.jiraCloudProjectSettings.isSyncDone}
											/>
											<RadioButton
												text={intl.formatMessage({id: 'common.no'})}
												onSelected={() => selectSyncRemainingTime(false)}
												value={false}
												stateValue={syncRemaining}
												locked={projectDoneOrHalted || !project.jiraCloudProjectSettings.isSyncDone}
											/>
										</SyncRemainingSection>
									</>
								)}
						</div>
					) : (
						<FormattedMessage
							id="settings.jira.linked_to_epics"
							values={{
								name: (
									<b>
										{jiraEpics && jiraEpics.length > 0
											? jiraEpics.map(epic => epic.key).join(', ')
											: 'Loading...'}
									</b>
								),
							}}
						/>
					)}
					{project.jiraCloudEpicIds ? (
						<Button
							onClick={() => editJiraEpics()}
							variant={Button.VARIANT.GREEN_FILLED}
							disabled={projectDoneOrHalted || !project.jiraCloudProjectSettings.isSyncDone}
							title={
								projectDoneOrHalted
									? 'Cannot edit linked epics on Done or Halted projects'
									: intl.formatMessage({id: 'integrations.jira.edit_epics'})
							}
						>
							{intl.formatMessage({id: 'integrations.jira.edit_epics'})}
						</Button>
					) : (
						<>
							{hasFeatureFlag('edit_jira_cloud_project_sync_settings') && (
								<Button onClick={() => syncJiraProject('project')} variant={Button.VARIANT.GREEN_FILLED}>
									{intl.formatMessage({id: 'common.edit'})}
								</Button>
							)}
						</>
					)}
					<CutoffDateSection>
						Exclude items with due date before:
						<DateControls>
							<Checkbox
								checked={useCutoffDate}
								onChange={setUseCutoffDateHandler}
								disabled={!project.jiraCloudProjectSettings.isSyncDone}
							/>
							<DatePickerV3
								startDate={cutoffDate}
								handleDateRangeChange={setCutoffDateHandler}
								datePickerStyle={DATE_PICKER_STYLE.STANDARD}
								isSingleDatePicker={true}
								disabled={!useCutoffDate || !project.jiraCloudProjectSettings.isSyncDone}
							/>
						</DateControls>
					</CutoffDateSection>
					<br />
					<Button onClick={() => handleUnlinkFromJira()} variant={Button.VARIANT.RED_OUTLINE}>
						{intl.formatMessage({id: 'settings.unlink_jira'})}
					</Button>
				</>
			) : (
				<>
					<Button onClick={() => syncJiraProject('project')} variant={Button.VARIANT.GREEN_FILLED}>
						{intl.formatMessage({id: 'integrations.jira.sync_to_jira_project'})}
					</Button>
					<br />
					<Button onClick={() => syncJiraProject('epics')} variant={Button.VARIANT.GREEN_FILLED}>
						{intl.formatMessage({id: 'integrations.jira.sync_to_jira_epics'})}
					</Button>
				</>
			)}
		</div>
	);
};

export default JiraIntegrationSection;
